import { Language } from "@faire/web-api/indigofair/languages/Language";
import { Column, Flex, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { observer } from "mobx-react";
import React, { Suspense, useMemo } from "react";
import styled from "styled-components";

import { LazyLanguageSelector } from "@faire/retailer/components/LanguageSelector/LazyLanguageSelector";
import { LazyMobileLanguageSelector } from "@faire/retailer/components/LanguageSelector/LazyMobileLanguageSelector";
import { getIsBrandPreview } from "@faire/retailer/lib/getIsBrandPreview";
import { getIsRobot } from "@faire/retailer/serialized-data/getIsRobot";
import { getSessionCountry } from "@faire/retailer/serialized-data/getSessionCountry";
import { getSettingUseAdyenForRetailerPayments } from "@faire/retailer/settings/getSettingUseAdyenForRetailerPayments";
import { RetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { UserStore } from "@faire/retailer/stores/domain/UserStore";

import { PaymentMethodIcons } from "../PaymentMethodIcons";

import {
  DESKTOP_AND_ABOVE_FOOTER_HORIZ_PADDING,
  MOBILE_FOOTER_HORIZ_PADDING,
  TABLET_FOOTER_HORIZ_PADDING,
} from "./consts";
import { LegalLinks } from "./LegalLinks";

const FooterBottomBase = () => {
  return (
    <FooterWrapper>
      <FooterElements />
    </FooterWrapper>
  );
};

export const FooterBottom = observer(FooterBottomBase);

const LanguageSelector = () => {
  const { isMobile } = useViewport();
  const showLanguageSelector =
    !getIsBrandPreview() &&
    (RetailerStore.get().showLanguageSelector ||
      RetailerStore.get().showSEOLanguageSelector);

  // Avoid re-renders.
  const languageSelector = useMemo(() => {
    if (!showLanguageSelector) {
      return null;
    }
    return isMobile ? (
      <LanguageSelectorWrapper>
        <Suspense fallback={null}>
          <LazyMobileLanguageSelector
            displayFullLanguageName
            eventKey="footer"
            browserLanguage={UserStore.get().browserLevelLanguage as Language}
            browserLocale={UserStore.get().browserLevelLocaleKey}
            availableLocales={
              RetailerStore.get().availableLanguageSelectorLocales
            }
            assignLocalizedUrl
            showCountries={getIsRobot()}
            sessionCountry={getSessionCountry()}
          />
        </Suspense>
      </LanguageSelectorWrapper>
    ) : (
      <LanguageSelectorWrapper $noMargin>
        <Suspense fallback={null}>
          <LazyLanguageSelector
            displayFullLanguageName
            position="up"
            eventKey="footer"
            browserLanguage={UserStore.get().browserLevelLanguage as Language}
            browserLocale={UserStore.get().browserLevelLocaleKey}
            availableLocales={
              RetailerStore.get().availableLanguageSelectorLocales
            }
            assignLocalizedUrl
            showCountries={getIsRobot()}
            sessionCountry={getSessionCountry()}
          />
        </Suspense>
      </LanguageSelectorWrapper>
    );
  }, [isMobile, showLanguageSelector]);

  return languageSelector;
};

const PaymentMethodIconsWrapper = () => {
  const { isDesktopAndAbove } = useViewport();
  return getSettingUseAdyenForRetailerPayments() ? (
    <>
      <Spacer size={isDesktopAndAbove ? "4x" : "1x"} />
      <PaymentMethodIcons />
    </>
  ) : null;
};

const FooterElements = () => {
  const { isMobile, isTablet } = useViewport();

  if (isMobile) {
    return (
      <>
        <LanguageSelector />
        <LegalLinks />
        <PaymentMethodIconsWrapper />
      </>
    );
  }

  if (isTablet) {
    return (
      <>
        <Column>
          <LegalLinks />
          <PaymentMethodIconsWrapper />
        </Column>
        <LanguageSelector />
      </>
    );
  }

  return (
    <>
      <LegalLinks />
      <Row>
        <LanguageSelector />
        <PaymentMethodIconsWrapper />
      </Row>
    </>
  );
};

const LanguageSelectorWrapper = styled.div<{ $noMargin?: boolean }>`
  margin-top: ${(props) => (props.$noMargin ? "0" : "-20px")};
`;

const FooterWrapper = styled(Flex).attrs({
  justify: "space-between",
  align: { mobileAndAbove: "flex-start", tabletAndAbove: "center" },
  direction: { mobileAndAbove: "column", tabletAndAbove: "row" },
})`
  padding: 24px ${MOBILE_FOOTER_HORIZ_PADDING}px 32px;

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: 24px ${TABLET_FOOTER_HORIZ_PADDING}px 32px;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    padding: 24px ${DESKTOP_AND_ABOVE_FOOTER_HORIZ_PADDING}px 32px;
  }
`;
